import {
  EXAM_BALANCE_PLAN_LABEL,
  EXAM_SESSION_LABEL,
  EXAM_TYPE_LABEL,
} from '@/features/teacher/exams/constants';
import { periodRange, periodTitle, WEEKDAY_SHORT } from '@/features/teacher/components/timetable/timetableCopy';
import { downloadCsv } from '@/lib/download';
import { capitalizeWords } from '@/lib/format';
import type { Exam } from '@/types/exam';
import type { TimetablePeriod, Weekday } from '@/types/timetable';
import type { SectionWeekEntry } from '@/types/timetableAdmin';

/**
 * What the Export button writes.
 *
 * It used to write nothing — the button raised a toast that said the file was
 * on its way and no file ever was. The rows are already in the browser and are
 * already scoped to this institute, so the file is built here rather than at a
 * new endpoint (`lib/download.ts` carries the escaping).
 *
 * The exported grid is what is on screen, filters and all: an office that has
 * narrowed the week to one teacher is asking for that teacher's week, and a
 * file that quietly widened it back out would be the wrong answer to a question
 * they have already asked twice.
 */

/** Punctuation a file system will not take, and runs of spaces. */
function toFileName(parts: string[], extension: string): string {
  const stem = parts
    .join('-')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

  return `${stem || 'timetable'}.${extension}`;
}

export interface WeekExport {
  sectionName: string;
  /** "17 Aug – 23 Aug 2026" — printed in the file so a saved copy is dated. */
  rangeLabel: string;
  workingDays: Weekday[];
  periods: TimetablePeriod[];
  entries: SectionWeekEntry[];
}

/** One row per period, one column per working day — the grid as it is drawn. */
export function exportWeekCsv({
  sectionName,
  rangeLabel,
  workingDays,
  periods,
  entries,
}: WeekExport): void {
  const header = ['Period', 'Time', ...workingDays.map((day) => WEEKDAY_SHORT[day])];

  const rows = periods.map((period) => {
    if (period.kind === 'BREAK') {
      return [
        periodTitle(period),
        periodRange(period.startTime, period.endTime),
        // One cell repeated rather than a merged one: a spreadsheet reading
        // this back has no concept of the colspan the screen uses.
        ...workingDays.map(() => periodTitle(period)),
      ];
    }

    return [
      periodTitle(period),
      periodRange(period.startTime, period.endTime),
      ...workingDays.map((day) => {
        const entry = entries.find(
          (item) => item.dayOfWeek === day && item.period.uuid === period.uuid,
        );
        if (!entry) return '';
        return entry.teacher ? `${capitalizeWords(entry.subject.name)} (${entry.teacher})` : capitalizeWords(entry.subject.name);
      }),
    ];
  });

  downloadCsv(toFileName([sectionName, 'timetable', rangeLabel], 'csv'), [
    [`${sectionName} — weekly timetable`, rangeLabel],
    [],
    header,
    ...rows,
  ]);
}

/** One row per paper, in the order the table shows them. */
export function exportExamsCsv(sectionName: string, exams: Exam[]): void {
  const rows = exams.map((exam) => [
    exam.examDate,
    exam.session ? EXAM_SESSION_LABEL[exam.session] : '',
    exam.startTime ?? '',
    exam.endTime ?? '',
    capitalizeWords(exam.subject.name),
    exam.name,
    EXAM_TYPE_LABEL[exam.examType],
    exam.maxMarks,
    exam.passMarks,
    exam.balancePlan ? EXAM_BALANCE_PLAN_LABEL[exam.balancePlan] : '',
  ]);

  downloadCsv(toFileName([sectionName, 'exam-timetable'], 'csv'), [
    [`${sectionName} — examination timetable`],
    [],
    [
      'Date',
      'Session',
      'Starts',
      'Ends',
      'Subject',
      'Paper',
      'Type',
      'Max marks',
      'Pass marks',
      'Rest of the day',
    ],
    ...rows,
  ]);
}
